/**
 * FlowAI Workspace-Detail — 7 Tabs:
 *   Overview · Settings · Members · Playbooks · Skills · Tool-Permissions · Audit
 *
 * @page /flowai/workspaces/:id
 * @since v2.484.0
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, Card, Button, Badge } from '../../components/ui';
import FlowAIBreadcrumb from '../../components/flowai/FlowAIBreadcrumb';

const API = import.meta.env.VITE_API_URL || '';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'settings', label: 'Settings' },
  { key: 'members', label: 'Members' },
  { key: 'playbooks', label: 'Playbooks' },
  { key: 'skills', label: 'Skills' },
  { key: 'tools', label: 'Tool-Permissions' },
  { key: 'audit', label: 'Audit-Trail' },
];

export default function FlowAIWorkspaceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [ws, setWs] = useState(null);

  async function load() {
    const r = await fetch(`${API}/api/flowai/workspaces/${id}`, { credentials: 'include' });
    if (!r.ok) return;
    const d = await r.json().catch(() => ({}));
    setWs(d?.workspace ?? d ?? null);
  }
  useEffect(() => { load(); }, [id]);

  if (!ws) return <div className="p-6 text-sm">Lade Workspace…</div>;

  return (
    <div className="p-6">
      <FlowAIBreadcrumb trail={[{ label: 'Workspaces', to: '/flowai/workspaces' }, { label: ws.name }]} />
      <PageHeader
        title={ws.name}
        subtitle={ws.description}
        actions={<Button variant="ghost" onClick={() => navigate('/flowai/workspaces')}>← Liste</Button>}
      />

      <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-subtle,#E5E7EB)] inline-flex mb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 text-[12px] rounded-[7px] ${tab === t.key ? 'bg-white shadow-sm font-medium' : 'text-[var(--text-secondary,#4B5563)]'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab ws={ws} />}
      {tab === 'settings' && <SettingsTab workspaceId={id} />}
      {tab === 'members' && <MembersTab workspaceId={id} />}
      {tab === 'playbooks' && <PlaybooksTab workspaceId={id} />}
      {tab === 'skills' && <SkillsTab workspaceId={id} />}
      {tab === 'tools' && <ToolPermissionsTab workspaceId={id} />}
      {tab === 'audit' && <AuditTab workspaceId={id} />}
    </div>
  );
}

function OverviewTab({ ws }) {
  const ownerText = ws.owner_name
    ? `${ws.owner_name}${ws.owner_user_id ? ` (#${ws.owner_user_id})` : ''}`
    : (ws.owner_user_id ? `#${ws.owner_user_id}` : 'Nicht zugewiesen');
  return (
    <Card>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <Stat label="Pilot-Mode" value={ws.pilot_mode ? 'ON' : 'OFF'} />
        <Stat label="Sandbox-Mode" value={ws.sandbox_mode ? 'ON' : 'OFF'} />
        <Stat label="Playbooks" value={Number(ws.playbook_count) || 0} />
        <Stat label="Skills" value={Number(ws.skill_count) || 0} />
      </div>
      <div className="mt-4 text-[12px] text-[var(--text-secondary,#4B5563)]">
        Owner: {ownerText} · Erstellt: {ws.created_at ? new Date(ws.created_at).toLocaleString('de-AT', { timeZone: 'Europe/Vienna' }) : '—'}
      </div>
    </Card>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-[var(--text-tertiary,#6B7280)] mb-1">{label}</div>
      <div className="text-lg font-semibold text-[var(--text-primary,#1A1A2E)]">{value}</div>
    </div>
  );
}

function SettingsTab({ workspaceId }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    const r = await fetch(`${API}/api/flowai/workspaces/${workspaceId}/settings`, { credentials: 'include' });
    const d = await r.json().catch(() => ({}));
    setSettings(d?.settings ?? d ?? {});
  }
  useEffect(() => { load(); }, [workspaceId]);

  async function save(patch) {
    setSaving(true);
    const next = { ...settings, ...patch };
    const r = await fetch(`${API}/api/flowai/workspaces/${workspaceId}/settings`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(next),
    });
    if (r.ok) {
      const d = await r.json().catch(() => ({}));
      setSettings(d?.settings ?? d ?? settings);
    }
    setSaving(false);
  }

  if (!settings) return <Card>Lade…</Card>;

  return (
    <div className="space-y-4">
      <Card title="Mode-Flags">
        <ToggleRow label="Pilot-Mode (begrenzte Kunden-Whitelist)" value={settings.pilot_mode} onChange={(v) => save({ pilot_mode: v })} disabled={saving} />
        <ToggleRow label="Sandbox-Mode (Mails an Test-Empfänger umleiten)" value={settings.sandbox_mode} onChange={(v) => save({ sandbox_mode: v })} disabled={saving} />
        {settings.sandbox_mode && (
          <div className="mt-2 pl-1">
            <label className="text-[11px] text-[var(--text-tertiary,#6B7280)]">Sandbox-Empfänger (Pflicht bei Sandbox AN)</label>
            <input
              type="email"
              required
              defaultValue={settings.sandbox_redirect_email || 'manuel.wagner@weship.eu'}
              onBlur={(e) => save({ sandbox_redirect_email: e.target.value })}
              placeholder="manuel.wagner@weship.eu"
              className="w-full px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm mt-1"
            />
          </div>
        )}
      </Card>

      <Card title="Delivery-Mode (B2B-Notifications, #1086)">
        <DeliveryModeRow
          value={settings.delivery_mode || 'sandbox'}
          onChange={(v) => save({ delivery_mode: v })}
          disabled={saving}
        />
        <div className="text-[11px] text-[var(--text-tertiary,#6B7280)] mt-2 leading-snug">
          <strong>sandbox</strong>: Mails an Sandbox-Empfänger umleiten (alle Companies erlaubt).{' '}
          <strong>live</strong>: echte Versendung an Companies aus der Self-Service Registry (siehe Card unten).
          <br />
          Die Liste der erlaubten Companies pflegt sich seit v2.547.0 (#1257) selbst — siehe nächste Card.
        </div>
      </Card>

      <B2BRegistryCard />


      <Card title="Approver-Rollen">
        <RoleRow label="Primary" value={settings.approver_primary_role} onChange={(v) => save({ approver_primary_role: v })} />
        <RoleRow label="Fallback" value={settings.approver_primary_fallback} onChange={(v) => save({ approver_primary_fallback: v })} />
        <RoleRow label="Escalation" value={settings.approver_escalation_role} onChange={(v) => save({ approver_escalation_role: v })} />
        <div className="text-[11px] text-[var(--text-tertiary,#6B7280)] mt-2">
          Rollen-Keys: <code>workspace_owner</code>, <code>workspace_members</code>, <code>case_owner</code>, oder <code>users.rolle</code>-Wert.
        </div>
      </Card>

      <Card title="Spam-Limits">
        <NumberRow label="Pro Case + Stage" value={settings.spam_protection_per_case_per_stage ?? 1} onChange={(v) => save({ spam_protection_per_case_per_stage: v })} />
        <NumberRow label="Pro Empfänger / Tag" value={settings.spam_protection_per_recipient_per_day ?? 3} onChange={(v) => save({ spam_protection_per_recipient_per_day: v })} />
        <NumberRow label="Pro Empfänger / Tag (global)" value={settings.spam_protection_per_recipient_per_day_global ?? 5} onChange={(v) => save({ spam_protection_per_recipient_per_day_global: v })} />
      </Card>

      <Card title="Approval-Timeout">
        <NumberRow label="Escalation (Minuten)" value={settings.approver_escalation_minutes ?? 30} onChange={(v) => save({ approver_escalation_minutes: v })} />
      </Card>
    </div>
  );
}

function ToggleRow({ label, value, onChange, disabled }) {
  return (
    <label className="flex items-center justify-between py-2">
      <span className="text-sm">{label}</span>
      <input type="checkbox" checked={!!value} disabled={disabled} onChange={(e) => onChange?.(e.target.checked)} />
    </label>
  );
}
function RoleRow({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="text-[11px] uppercase text-[var(--text-tertiary,#6B7280)] w-28">{label}</div>
      <input
        defaultValue={value || ''}
        onBlur={(e) => onChange?.(e.target.value)}
        placeholder="z.B. cs_teamlead"
        className="flex-1 px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm"
      />
    </div>
  );
}
function DeliveryModeRow({ value, onChange, disabled }) {
  const opts = ['sandbox', 'allow_list', 'live'];
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="text-sm flex-1">Mode</div>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value)}
        className="px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm"
      >
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

// #1257 — Self-Service B2B Company-Registry
// Liest GET /api/flowai/b2b-registry (workspace-unabhängig, globaler State)
// Toggle pro Company schaltet manual_override_disabled. Resync-Button forciert
// einen OMS-Odoo-Sync (Loop läuft sonst alle 5min).
function B2BRegistryCard() {
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);

  async function load() {
    const r = await fetch(`${API}/api/flowai/b2b-registry`, { credentials: 'include' });
    const d = await r.json().catch(() => ({}));
    setRows(Array.isArray(d?.rows) ? d.rows : []);
  }
  useEffect(() => { load(); }, []);

  async function toggle(id, currentDisabled) {
    setBusyId(id);
    await fetch(`${API}/api/flowai/b2b-registry/${id}/override`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ disabled: !currentDisabled }),
    });
    setBusyId(null);
    load();
  }

  // #1165 — Feature-Toggle (wareneingang | retoure) pro Company
  async function toggleFeature(id, feature, nextEnabled) {
    setBusyId(`${id}-${feature}`);
    await fetch(`${API}/api/flowai/b2b-registry/${id}/feature/${feature}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: nextEnabled }),
    });
    setBusyId(null);
    load();
  }

  // #1165 — WE-States persistieren (Whitelist aus 6 Werten, im Service gefiltert)
  async function saveWeStates(id, states) {
    setBusyId(`${id}-westates`);
    await fetch(`${API}/api/flowai/b2b-registry/${id}/wareneingang-states`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ states }),
    });
    setBusyId(null);
    load();
  }

  async function triggerSync() {
    setSyncing(true);
    setSyncMsg(null);
    const r = await fetch(`${API}/api/flowai/b2b-registry/sync`, {
      method: 'POST',
      credentials: 'include',
    });
    const d = await r.json().catch(() => ({}));
    setSyncing(false);
    if (d?.ok) setSyncMsg(`✓ Sync OK — ${d.upserted} Companies aktualisiert (${d.enabled} OMS-enabled).`);
    else setSyncMsg(`⚠ Sync fehlgeschlagen: ${d?.error || 'unknown'}`);
    load();
  }

  if (rows === null) return <Card title="B2B Company-Registry (#1257)">Lade…</Card>;

  const effectiveCount = rows.filter((r) => r.effective_allowed).length;

  return (
    <Card title="B2B Company-Registry (Self-Service Allow-List, #1257)">
      <div className="mb-3 p-3 rounded border border-blue-200 bg-blue-50 text-blue-900 text-[12px] leading-snug">
        <strong>Datenquelle:</strong> <code>res.company.cases_notification_enabled</code> aus OMS-Odoo.
        Sync alle 5 min (Worker). Effektiv erlaubt = OMS-Flag <strong>AND</strong> kein manueller Override.
        Bei leerer Registry oder Sync-Failure: <strong>DEFAULT-DENY</strong> — keine B2B-Notifications.
      </div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm">
          <strong>{effectiveCount}</strong> Companies effektiv erlaubt
          {' '}<span className="text-[var(--text-tertiary,#6B7280)]">/ {rows.length} insgesamt synced</span>
        </div>
        <button
          type="button"
          onClick={triggerSync}
          disabled={syncing}
          className="px-3 py-1.5 text-sm rounded border border-[var(--border-subtle,#E5E7EB)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50"
        >
          {syncing ? 'Sync läuft…' : 'Resync from OMS'}
        </button>
      </div>
      {syncMsg && <div className="text-[12px] mb-2">{syncMsg}</div>}
      <div className="mb-3 p-3 rounded border border-amber-200 bg-amber-50 text-amber-900 text-[12px] leading-snug">
        <strong>#1165 Wareneingang + Retoure (DSL v2):</strong> Eigene Toggles pro Company.
        Beim ersten Enable wird ein Cold-Start-Seed ausgeführt (alle aktuellen Pickings werden ohne Notification indexiert).
        Erst danach lösen Status-Wechsel B2B-Notifications aus. WE-Übergänge sind pro Company konfigurierbar.
      </div>
      {rows.length === 0 ? (
        <div className="p-4 text-center text-sm text-[var(--text-tertiary,#6B7280)] border border-dashed border-[var(--border-subtle,#E5E7EB)] rounded">
          Registry leer — Worker hat noch nicht synced. „Resync from OMS" klicken.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase text-[var(--text-tertiary,#6B7280)] border-b border-[var(--border-subtle,#E5E7EB)]">
                <th className="text-left py-2 pl-1 w-12">ID</th>
                <th className="text-left py-2">Company</th>
                <th className="text-center py-2 w-20">OMS-Cases</th>
                <th className="text-center py-2 w-20">Manuell aus</th>
                <th className="text-center py-2 w-24">Cases eff.</th>
                <th className="text-center py-2 w-20">WE</th>
                <th className="text-center py-2 w-20">Retoure</th>
                <th className="text-left py-2 min-w-[220px]">WE-Übergänge</th>
                <th className="text-right py-2 w-16">Mails</th>
                <th className="text-right py-2 pr-1 w-20">Letzte</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const effective = r.effective_allowed;
                const omsOff = !r.oms_notification_enabled;
                const manualOff = r.manual_override_disabled;
                const statusColor = effective ? 'text-green-700 bg-green-50'
                  : omsOff ? 'text-gray-600 bg-gray-50'
                  : 'text-amber-700 bg-amber-50';
                const statusLabel = effective ? '✓' : omsOff ? 'off' : 'manuell';
                return (
                  <tr key={r.odoo_company_id} className="border-b border-[var(--border-subtle,#E5E7EB)] last:border-0 align-middle">
                    <td className="py-2 pl-1 font-mono text-[12px] text-[var(--text-tertiary,#6B7280)]">{r.odoo_company_id}</td>
                    <td className="py-2">{r.company_name || <span className="text-[var(--text-tertiary,#6B7280)] italic">(ohne Name)</span>}</td>
                    <td className="py-2 text-center">
                      {r.oms_notification_enabled
                        ? <span className="text-green-600">✓</span>
                        : <span className="text-[var(--text-tertiary,#6B7280)]">—</span>}
                    </td>
                    <td className="py-2 text-center">
                      <input
                        type="checkbox"
                        checked={manualOff}
                        disabled={busyId === r.odoo_company_id}
                        onChange={() => toggle(r.odoo_company_id, manualOff)}
                      />
                    </td>
                    <td className="py-2 text-center">
                      <span className={`text-[11px] px-2 py-0.5 rounded ${statusColor}`}>{statusLabel}</span>
                    </td>
                    <td className="py-2 text-center">
                      <FeatureToggle
                        enabled={!!r.wareneingang_notification_enabled}
                        effective={!!r.wareneingang_effective}
                        busy={busyId === `${r.odoo_company_id}-wareneingang`}
                        activeSince={r.wareneingang_active_since}
                        onToggle={(next) => toggleFeature(r.odoo_company_id, 'wareneingang', next)}
                      />
                    </td>
                    <td className="py-2 text-center">
                      <FeatureToggle
                        enabled={!!r.retoure_notification_enabled}
                        effective={!!r.retoure_effective}
                        busy={busyId === `${r.odoo_company_id}-retoure`}
                        activeSince={r.retoure_active_since}
                        onToggle={(next) => toggleFeature(r.odoo_company_id, 'retoure', next)}
                      />
                    </td>
                    <td className="py-2">
                      <WeStatesEditor
                        states={Array.isArray(r.wareneingang_states) ? r.wareneingang_states : []}
                        busy={busyId === `${r.odoo_company_id}-westates`}
                        onSave={(next) => saveWeStates(r.odoo_company_id, next)}
                      />
                    </td>
                    <td className="py-2 text-right font-mono text-[12px]">{r.notification_count ?? 0}</td>
                    <td className="py-2 pr-1 text-right text-[11px] text-[var(--text-tertiary,#6B7280)]">
                      {r.last_notification_at
                        ? new Date(r.last_notification_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
                        : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// #1165 — Feature-Toggle mit Effektiv-Indikator + active_since-Tooltip
function FeatureToggle({ enabled, effective, busy, activeSince, onToggle }) {
  const tooltip = activeSince
    ? `Aktiv seit ${new Date(activeSince).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
    : 'noch nie aktiviert';
  return (
    <div className="flex flex-col items-center gap-0.5" title={tooltip}>
      <input
        type="checkbox"
        checked={enabled}
        disabled={busy}
        onChange={(e) => onToggle(e.target.checked)}
      />
      {enabled && !effective && (
        <span className="text-[9px] text-amber-600">override</span>
      )}
    </div>
  );
}

// #1165 — WE-States Multi-Toggle (6 Werte, sofort persistieren beim Wechsel)
const WE_STATE_OPTIONS = [
  { key: 'draft', label: 'Entwurf' },
  { key: 'waiting', label: 'Wartend' },
  { key: 'confirmed', label: 'Bestätigt' },
  { key: 'assigned', label: 'Bereit' },
  { key: 'done', label: 'Erledigt' },
  { key: 'cancel', label: 'Abgebrochen' },
];

function WeStatesEditor({ states, busy, onSave }) {
  const set = new Set(states);
  function toggleState(key) {
    if (busy) return;
    const next = new Set(set);
    if (next.has(key)) next.delete(key); else next.add(key);
    onSave(Array.from(next));
  }
  return (
    <div className="flex flex-wrap gap-1">
      {WE_STATE_OPTIONS.map((o) => {
        const active = set.has(o.key);
        return (
          <button
            key={o.key}
            type="button"
            disabled={busy}
            onClick={() => toggleState(o.key)}
            className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors ${
              active
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-[var(--border-subtle,#E5E7EB)] text-[var(--text-tertiary,#6B7280)] hover:bg-[var(--bg-inset,#F8F9FC)]'
            } disabled:opacity-50`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function NumberRow({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="text-sm flex-1">{label}</div>
      <input
        type="number"
        defaultValue={value ?? ''}
        onBlur={(e) => onChange?.(Number(e.target.value))}
        className="w-24 px-2 py-1 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm text-right"
      />
    </div>
  );
}

function MembersTab({ workspaceId }) {
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState('member');

  async function load() {
    const [mRes, uRes] = await Promise.all([
      fetch(`${API}/api/flowai/workspaces/${workspaceId}/members`, { credentials: 'include' }).then((r) => r.json()).catch(() => ({})),
      fetch(`${API}/api/users`, { credentials: 'include' }).then((r) => (r.ok ? r.json() : [])).catch(() => []),
    ]);
    setMembers(Array.isArray(mRes) ? mRes : (mRes?.members ?? []));
    const list = Array.isArray(uRes) ? uRes : [];
    setUsers(list.filter((u) => u.aktiv !== false));
  }
  useEffect(() => { load(); }, [workspaceId]);

  const memberIds = useMemo(() => new Set(members.map((m) => m.user_id)), [members]);
  const eligibleUsers = useMemo(() => users.filter((u) => !memberIds.has(u.id)), [users, memberIds]);

  async function add() {
    if (!userId) return;
    await fetch(`${API}/api/flowai/workspaces/${workspaceId}/members`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: Number(userId), role }),
    });
    setUserId('');
    load();
  }
  async function remove(uid) {
    if (!confirm('Member entfernen?')) return;
    await fetch(`${API}/api/flowai/workspaces/${workspaceId}/members/${uid}`, { method: 'DELETE', credentials: 'include' });
    load();
  }

  return (
    <Card title="Members">
      <div className="flex gap-2 mb-3 flex-wrap">
        <select
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          className="px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm min-w-[280px]"
        >
          <option value="">— User wählen —</option>
          {eligibleUsers.map((u) => (
            <option key={u.id} value={u.id}>
              {(u.vorname || '') + ' ' + (u.nachname || '')} ({u.email})
            </option>
          ))}
        </select>
        <select value={role} onChange={(e) => setRole(e.target.value)} className="px-3 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-sm">
          <option value="workspace_owner">Workspace Owner</option>
          <option value="member">Member</option>
          <option value="approver">Approver</option>
          <option value="viewer">Viewer</option>
        </select>
        <Button onClick={add} disabled={!userId}>Hinzufügen</Button>
      </div>
      <div className="divide-y divide-[var(--border-subtle,#E5E7EB)]">
        {members.map((m) => {
          const fullName = [m.vorname, m.nachname].filter(Boolean).join(' ').trim();
          return (
            <div key={m.user_id} className="py-2 flex items-center gap-3 text-sm">
              <span className="font-mono text-[var(--text-tertiary,#6B7280)] w-10">#{m.user_id}</span>
              <span>{fullName || m.email || '—'}</span>
              {m.email && fullName && <span className="text-[11px] text-[var(--text-tertiary,#6B7280)]">{m.email}</span>}
              <Badge>{m.role}</Badge>
              <button onClick={() => remove(m.user_id)} className="ml-auto text-[11px] text-red-600 hover:underline">Entfernen</button>
            </div>
          );
        })}
        {members.length === 0 && <div className="py-4 text-[12px] text-[var(--text-tertiary,#6B7280)]">Noch keine Members.</div>}
      </div>
    </Card>
  );
}

function PlaybooksTab({ workspaceId }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/flowai/playbooks?workspace_id=${workspaceId}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setRows(Array.isArray(d) ? d : (d?.playbooks ?? [])))
      .catch(() => setRows([]));
  }, [workspaceId]);
  return (
    <Card title="Playbooks" actions={<Button onClick={() => navigate(`/flowai/playbooks/new?workspace_id=${workspaceId}`)}>+ Neu</Button>}>
      <div className="divide-y divide-[var(--border-subtle,#E5E7EB)]">
        {rows.map((p) => (
          <div key={p.id} className="py-2 flex items-center gap-3 text-sm">
            <span className="font-mono text-[var(--text-tertiary,#6B7280)]">#{p.id}</span>
            <span className="font-medium">{p.name}</span>
            {p.version && <Badge>v{p.version}</Badge>}
            <Badge variant={p.enabled ? 'success' : 'default'}>{p.enabled ? 'aktiv' : 'inaktiv'}</Badge>
            <Button variant="ghost" className="ml-auto" onClick={() => navigate(`/flowai/playbooks/${p.id}`)}>Öffnen</Button>
          </div>
        ))}
        {rows.length === 0 && <div className="py-4 text-[12px] text-[var(--text-tertiary,#6B7280)]">Noch keine Playbooks.</div>}
      </div>
    </Card>
  );
}

function SkillsTab({ workspaceId }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/flowai/skills?workspace_id=${workspaceId}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setRows(Array.isArray(d) ? d : (d?.skills ?? [])))
      .catch(() => setRows([]));
  }, [workspaceId]);
  return (
    <Card title="Skills" actions={<Button onClick={() => navigate(`/flowai/skills/new?workspace_id=${workspaceId}`)}>+ Neu</Button>}>
      <div className="divide-y divide-[var(--border-subtle,#E5E7EB)]">
        {rows.map((s) => (
          <div key={s.id} className="py-2 flex items-center gap-3 text-sm">
            <span className="font-mono text-[var(--text-tertiary,#6B7280)]">#{s.id}</span>
            <span className="font-medium">{s.name}</span>
            <code className="text-[11px] text-[var(--text-tertiary,#6B7280)]">{s.slug}</code>
            {s.version && <Badge>v{s.version}</Badge>}
            <Button variant="ghost" className="ml-auto" onClick={() => navigate(`/flowai/skills/${s.id}`)}>Öffnen</Button>
          </div>
        ))}
        {rows.length === 0 && <div className="py-4 text-[12px] text-[var(--text-tertiary,#6B7280)]">Noch keine Skills.</div>}
      </div>
    </Card>
  );
}

function ToolPermissionsTab({ workspaceId }) {
  const [perms, setPerms] = useState([]);
  const [available, setAvailable] = useState([]);

  async function load() {
    const [pr, ar] = await Promise.all([
      fetch(`${API}/api/flowai/tool-permissions/${workspaceId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => ({})),
      fetch(`${API}/api/flowai/connections/available`, { credentials: 'include' }).then((r) => r.json()).catch(() => ({})),
    ]);
    setPerms(Array.isArray(pr) ? pr : (pr?.permissions ?? []));
    setAvailable(Array.isArray(ar) ? ar : (ar?.connections ?? []));
  }
  useEffect(() => { load(); }, [workspaceId]);

  const permByKey = useMemo(() => Object.fromEntries(perms.map((p) => [p.connection_key, p])), [perms]);

  async function toggle(key, enabled) {
    await fetch(`${API}/api/flowai/tool-permissions/${workspaceId}/${key}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    });
    load();
  }

  return (
    <Card title="Tool-Permissions" subtitle="Welche externen Konnektoren darf dieser Workspace nutzen?">
      <div className="divide-y divide-[var(--border-subtle,#E5E7EB)]">
        {available.map((c) => {
          const p = permByKey[c.connection_key];
          return (
            <div key={c.connection_key} className="py-2 flex items-center gap-3 text-sm">
              <span className="font-medium">{c.label}</span>
              <code className="text-[11px] text-[var(--text-tertiary,#6B7280)]">{c.connection_key}</code>
              <input type="checkbox" className="ml-auto" checked={!!p?.enabled} onChange={(e) => toggle(c.connection_key, e.target.checked)} />
            </div>
          );
        })}
        {available.length === 0 && <div className="py-4 text-[12px] text-[var(--text-tertiary,#6B7280)]">Keine Konnektoren verfügbar.</div>}
      </div>
    </Card>
  );
}

function AuditTab({ workspaceId }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/flowai/audit?workspace_id=${workspaceId}&limit=100`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setRows(Array.isArray(d) ? d : (d?.entries ?? [])))
      .catch(() => setRows([]));
  }, [workspaceId]);
  return (
    <Card title="Audit-Trail" className="w-full">
      <div className="divide-y divide-[var(--border-subtle,#E5E7EB)] max-h-[600px] overflow-y-auto">
        {rows.map((a) => (
          <div key={a.id} className="py-2 text-[12px]">
            <div className="flex items-center gap-2">
              <span className="font-mono text-[var(--text-tertiary,#6B7280)]">#{a.id}</span>
              <span className="font-medium">{a.action_type}</span>
              <Badge>{a.approval_status || a.status}</Badge>
              {a.related_case_id && <Badge variant="default">Case #{a.related_case_id}</Badge>}
              <span className="ml-auto text-[var(--text-tertiary,#6B7280)]">{new Date(a.created_at).toLocaleString()}</span>
            </div>
            {a.action_payload?.subject && (
              <div className="mt-1 text-[var(--text-secondary,#4B5563)]">{a.action_payload.subject}</div>
            )}
          </div>
        ))}
        {rows.length === 0 && (
          <div className="py-10 text-center">
            <div className="text-[12px] text-[var(--text-tertiary,#6B7280)]">Noch keine Audit-Einträge für diesen Workspace.</div>
          </div>
        )}
      </div>
    </Card>
  );
}
